import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { IdCard, CreditCard, Upload, Loader2, CheckCircle2, Clock3, XCircle, FileText } from "lucide-react";
import dayjs from "dayjs";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: "identification", label: "Identification Document", desc: "Passport / National ID / Emirates ID", icon: IdCard },
  { key: "drivers_license", label: "Driver's License", desc: "Driver's License / International Driver's Permit", icon: CreditCard },
  { key: "other_documents", label: "Other Documents — If Applicable", desc: "Home-country licence, UAE visa / visa page, supporting documents", icon: FileText },
];

const STATUS = {
  under_review: { label: "Under review", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock3 },
  verified: { label: "Verified", cls: "bg-[#f0f9f0] text-[#009900] border-[#009900]/30", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-600 border-red-200", icon: XCircle },
};

const uploadLabel = (doc) => {
  if (!doc) return "Upload";
  return doc.status === "verified" ? "Verified" : "Re-upload";
};

export const DocumentsPanel = () => {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (type, file) => {
    if (!file) return;
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("doc_type", type);
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — now under review");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(null);
    }
  };

  if (docs === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  const byType = Object.fromEntries(docs.map((d) => [d.doc_type, d]));

  return (
    <div data-testid="documents-panel">
      <p className="text-sm text-[#57685e] mb-5">
        Upload your documents for a faster, paper-free pickup. Status pipeline: <strong>Under review → Verified</strong> (or Rejected with a quick re-upload prompt). Camera or file upload — JPEG, PNG or PDF up to 8 MB.
      </p>
      <div className="grid md:grid-cols-3 gap-5">
        {DOC_TYPES.map((t) => {
          const doc = byType[t.key];
          const st = doc ? STATUS[doc.status] : null;
          const isImage = doc?.content_type?.startsWith("image/");
          return (
            <div key={t.key} className="bg-white border border-[#e2ebe4] rounded-lg p-5 flex flex-col" data-testid={`doc-card-${t.key}`}>
              <div className="flex items-center gap-2 mb-1">
                <t.icon className="w-5 h-5 text-[#009900]" />
                <h3 className="font-bold text-sm text-[#0F1E17]">{t.label}</h3>
              </div>
              <p className="text-xs text-[#57685e] mb-3">{t.desc}</p>

              {doc ? (
                <div className="mb-3">
                  {isImage ? (
                    <img src={`${API_BASE}/documents/${doc.doc_id}/file`} alt={t.label}
                      className="w-full h-28 object-cover rounded border border-[#e2ebe4]" data-testid={`doc-preview-${t.key}`} />
                  ) : (
                    <div className="w-full h-28 rounded border border-[#e2ebe4] bg-[#f4f7f4] flex flex-col items-center justify-center text-[#57685e]">
                      <FileText className="w-8 h-8 mb-1" />
                      <span className="text-[10px]">{doc.original_filename}</span>
                    </div>
                  )}
                  <div className={`mt-2 inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-[11px] font-semibold ${st.cls}`} data-testid={`doc-status-${t.key}`}>
                    <st.icon className="w-3.5 h-3.5" /> {st.label}
                  </div>
                  {doc.status === "rejected" && (
                    <p className="text-[11px] text-red-600 mt-1.5" data-testid={`doc-rejection-${t.key}`}>{doc.rejection_reason} — please re-upload.</p>
                  )}
                  <p className="text-[10px] text-[#8a978f] mt-1">Uploaded {dayjs(doc.uploaded_at).format("DD MMM YYYY, HH:mm")}</p>
                </div>
              ) : (
                <div className="mb-3 h-28 rounded border-2 border-dashed border-[#d5ddd5] flex items-center justify-center text-xs text-[#9aa79f]">
                  Not uploaded yet
                </div>
              )}

              <input type="file" accept="image/jpeg,image/png,image/webp,image/bmp,application/pdf" className="hidden"
                ref={(el) => (inputRefs.current[t.key] = el)}
                onChange={(e) => { upload(t.key, e.target.files?.[0]); e.target.value = ""; }}
                data-testid={`doc-file-input-${t.key}`} />
              <button
                onClick={() => inputRefs.current[t.key]?.click()}
                disabled={uploading === t.key || doc?.status === "verified"}
                className="mt-auto btn-cta rounded px-4 py-2.5 text-xs flex items-center justify-center gap-2"
                data-testid={`doc-upload-btn-${t.key}`}
              >
                {uploading === t.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploadLabel(doc)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
